import { useEffect, useState } from "react";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";
import { Site } from "@atlassiansox/asc-stargate";
import OriginTracing from "@atlassiansox/origin-tracing";

import { useSearchParameters } from "@/components/signup/hooks";
import { DirectBuyStatus } from "@/components/signup/post-order/src/components";
import { ErrorView } from "@/components/signup/post-order/src/components/error-view";
import { Loading } from "@/components/signup/post-order/src/components/page-loading";
import { hasCMKProduct } from "@/components/signup/post-order/src/layout/direct-buy-org-setup-container";
import { PostOrderProvisioningContainer } from "@/components/signup/post-order/src/layout/post-order-provisioning-container";
import {
    collectionOfferingIds,
    orgBasedOfferingIdMap,
    ORG_BASED_OFFERING_IDS
} from "@/components/signup/utils";
import {
    ufoEventPostOrderGetActivationType,
    OrgBasedProduct
} from "@/components/signup/utils";
import {
    OrderItem,
    useBxpWebStargateClient
} from "@/libraries/bxp-web-stargate-client/src";
import { getIdSigninUrl } from "@/libraries/signup/bxpkit-core";
import { checkFeatureFlagGate, featureFlags } from "@/utils/feature-flags";
import { useLoggedInUser } from "@/utils/logged-in-user-provider";

export const COMMA_REGEX = /,|%2C/;
export const COMMA = "%2C";

const CMK_SUPPORT_URL =
    "https://support.atlassian.com/security-and-access-policies/docs/set-up-cmk-enabled-atlassian-apps/";

export const BuyCloudPostOrderProvisioningPageTemplate = () => {
    return <BuyCloudPostOrderProvisioningComponent />;
};

const BuyCloudPostOrderProvisioningComponent = () => {
    // If user is not logged in, edirect user to the sign-in page

    const [isLoading, setIsLoading] = useState(true);
    const { orderId, transactionAccount, directBuyStatus } =
        useSearchParameters();
    const stargateClient = useBxpWebStargateClient();
    const { requestInProgress, isUserLoggedIn } = useLoggedInUser();
    const { createAnalyticsEvent } = useAnalyticsEvents();

    useEffect(() => {
        if (requestInProgress) {
            return;
        }

        if (!isUserLoggedIn) {
            // If user is not logged in, edirect user to the sign-in page
            const signInUserOrigin = new OriginTracing({
                product: "wac-post-order-login"
            });
            const redirectUrl = getIdSigninUrl(signInUserOrigin.encode());

            window.location.assign(redirectUrl);
            return;
        }

        const extractActivationTypeDetails = (orderItems: OrderItem[]) => {
            const orgCollectionEntitlementIds: string[] = [];
            const cmkEntitlementIds: string[] = [];
            const orgOfferingIds: string[] = [];

            const cmkOfferingIds = [
                ORG_BASED_OFFERING_IDS.CUSTOMER_MANAGED_KEYS,
                ORG_BASED_OFFERING_IDS.CUSTOMER_MANAGED_KEYS_BILLED_SEPARATELY
            ];

            const orgBasedProducts = orderItems
                .map((item) => {
                    const productKey = orgBasedOfferingIdMap[item.offeringId];
                    if (productKey) {
                        if (
                            item.entitlementId &&
                            collectionOfferingIds.has(item.offeringId)
                        ) {
                            orgCollectionEntitlementIds.push(
                                item.entitlementId
                            );
                        }
                        if (
                            item.entitlementId &&
                            cmkOfferingIds.includes(item.offeringId)
                        ) {
                            cmkEntitlementIds.push(item.entitlementId);
                        }
                        orgOfferingIds.push(item.offeringId);
                    }
                    return productKey;
                })
                .filter((product) => !!product);

            return {
                orgBasedProducts,
                orgCollectionEntitlementIds,
                cmkEntitlementIds,
                orgOfferingIds,
                orgId: orderItems?.[0]?.metadata?.orgId || ""
            };
        };

        Promise.all([
            stargateClient.getAvailableSites({ orderId, transactionAccount }),
            stargateClient.getActivationType({
                orderId,
                transactionAccount
            })
        ])
            .then((res) => {
                const [getAvailableSitesResponse, getActivationTypeResponse] =
                    res;

                const { activationType, orderItems, orgBasedSiteOrderItems } =
                    getActivationTypeResponse;
                const {
                    orgId,
                    orgCollectionEntitlementIds,
                    cmkEntitlementIds,
                    orgOfferingIds
                } = extractActivationTypeDetails(orderItems);

                const orgBasedProducts = orgOfferingIds
                    .map(
                        (offeringId) =>
                            orgBasedOfferingIdMap[
                                offeringId as keyof typeof orgBasedOfferingIdMap
                            ] as OrgBasedProduct
                    )
                    .filter((product) => !!product);

                const isCollectionProduct = orgOfferingIds.some((offeringId) =>
                    collectionOfferingIds.has(offeringId)
                );

                // Check if order contains provisionable site entitlement
                const isOrderContainsProvisionableSiteEntitlement =
                    getAvailableSitesResponse.containsProvisionableSiteEntitlement !==
                    false;
                const isOrderContainsOrgAndSiteProduct =
                    isCollectionProduct ||
                    (orgBasedProducts.length > 0 &&
                        orgBasedSiteOrderItems &&
                        orgBasedSiteOrderItems.length > 0);
                /* istanbul ignore next */
                let containsOrgAndSiteProduct =
                    isOrderContainsOrgAndSiteProduct &&
                    isOrderContainsProvisionableSiteEntitlement;

                // Use CMK entitlement ID if available, otherwise use collection entitlement ID
                const offerEntitlementId =
                    cmkEntitlementIds[0] || orgCollectionEntitlementIds[0];

                /* Start of temporarily disabled Commerce flow */
                //Retrieve org details to verify if org-setup step is already done, redirect to site-setup if so
                const handlePreselectOrgRedirection = (orgId: string) => {
                    if (!orgOfferingIds || !orgOfferingIds[0]) {
                        return;
                    }
                    const offeringId = orgOfferingIds[0];
                    stargateClient
                        .getSignupAccountOrgDetails({
                            orgId,
                            offeringIds: orgOfferingIds
                        })
                        .then((orgDetails) => {
                            if (
                                orgDetails.success &&
                                orgDetails.organization &&
                                orgDetails.organization
                                    .entitlementsByOfferingIds
                            ) {
                                const alreadyContainsEntitlement =
                                    offerEntitlementId &&
                                    orgDetails.organization.entitlementsByOfferingIds.some(
                                        (entitlements) =>
                                            entitlements.offeringId ===
                                                offeringId &&
                                            entitlements.entitlementIds &&
                                            entitlements.entitlementIds.includes(
                                                offerEntitlementId
                                            )
                                    );
                                if (alreadyContainsEntitlement) {
                                    // Check if this is a CMK order - redirect to support page instead of site-setup
                                    const isCMKOrder =
                                        hasCMKProduct(orgBasedProducts);
                                    const isSkipSiteSetupForCMKEnabled =
                                        checkFeatureFlagGate(
                                            featureFlags.signupSkipSiteSetupForCMK
                                        );
                                    if (
                                        isCMKOrder &&
                                        isOrderContainsOrgAndSiteProduct &&
                                        isSkipSiteSetupForCMKEnabled
                                    ) {
                                        createAnalyticsEvent({
                                            eventName:
                                                "cmk-order-redirected-to-support",
                                            eventComponent:
                                                "post-order-provisioning",
                                            event: "redirected",
                                            eventType: "ui",
                                            action: "redirected",
                                            attributes: {
                                                offeringIds:
                                                    orgOfferingIds.join(","),
                                                orderId,
                                                orgId,
                                                alreadyActivated: true,
                                                redirectUrl: CMK_SUPPORT_URL
                                            }
                                        }).fire();
                                        window.location.assign(CMK_SUPPORT_URL);
                                        return;
                                    }
                                    redirectSiteSetup({
                                        offeringIds: orgOfferingIds,
                                        orgId,
                                        directBuyStatus
                                    });
                                    return;
                                }
                            }
                            redirectOrgSetup({
                                offeringIds: orgOfferingIds,
                                orgId,
                                entitlementId: offerEntitlementId,
                                directBuyStatus,
                                containsSiteProduct: containsOrgAndSiteProduct
                            });
                        });
                };
                /* End of temporarily disabled Commerce flow */

                // If orderItem contains existing site product entitlement, add siteId in queryparma to limit org selection on /org-setup
                const getExistingEntitlementSiteIds = () => {
                    const siteIds: string[] = [];
                    const filteredSites: Site[] = [];
                    if (!orgBasedSiteOrderItems) {
                        return {
                            siteIds,
                            isAllSiteEntitlementsProvisioned: false
                        };
                    }
                    const orderOrgBasedSiteEntitlementIds =
                        orgBasedSiteOrderItems.map(
                            (orderItem) => orderItem.entitlementId || ""
                        );
                    getAvailableSitesResponse.sites.forEach((site) => {
                        if (
                            site.entitlements.some((siteEntitlement) =>
                                orderOrgBasedSiteEntitlementIds.includes(
                                    siteEntitlement.ccpEntitlementId
                                )
                            )
                        ) {
                            siteIds.push(site.cloudId);
                            filteredSites.push(site);
                        }
                    });

                    // Check if all orgBasedSiteEntitlementIds are already provisioned
                    const isAllSiteEntitlementsProvisioned =
                        orderOrgBasedSiteEntitlementIds.length > 0 &&
                        orderOrgBasedSiteEntitlementIds.every(
                            (orderSiteEntitlementId) =>
                                filteredSites.some((site) =>
                                    site.entitlements.some(
                                        (entitlement) =>
                                            entitlement.ccpEntitlementId ===
                                            orderSiteEntitlementId
                                    )
                                )
                        );
                    return { siteIds, isAllSiteEntitlementsProvisioned };
                };

                const { siteIds, isAllSiteEntitlementsProvisioned } =
                    getExistingEntitlementSiteIds();
                if (isAllSiteEntitlementsProvisioned) {
                    containsOrgAndSiteProduct = false;
                }

                ufoEventPostOrderGetActivationType.addCustomData({
                    activationType,
                    orgId
                });
                if (activationType === "ORG") {
                    ufoEventPostOrderGetActivationType.complete();
                    if (orgId) {
                        handlePreselectOrgRedirection(orgId);
                    } else {
                        redirectOrgSetup({
                            offeringIds: orgOfferingIds,
                            entitlementId: offerEntitlementId,
                            siteIds,
                            directBuyStatus,
                            containsSiteProduct: containsOrgAndSiteProduct
                        });
                    }
                } else if (activationType === "SITE") {
                    ufoEventPostOrderGetActivationType.complete();
                    redirectSiteSetup({
                        offeringIds: orgOfferingIds,
                        directBuyStatus
                    });
                } else {
                    setIsLoading(false);
                    ufoEventPostOrderGetActivationType.error({
                        errorName: "Activation type not supported",
                        errorMessage: "Activation type not supported"
                    });
                }
            })
            .catch((error) => {
                setIsLoading(false);
                ufoEventPostOrderGetActivationType.error({
                    errorName: error.name,
                    errorMessage: error.message
                });
            });
    }, [requestInProgress]);

    const redirectOrgSetup = ({
        offeringIds,
        orgId,
        entitlementId,
        siteIds,
        directBuyStatus,
        containsSiteProduct
    }: {
        offeringIds: string[];
        orgId?: string;
        entitlementId?: string;
        siteIds?: string[];
        directBuyStatus?: DirectBuyStatus;
        containsSiteProduct?: boolean;
    }) => {
        const searchParams = new URLSearchParams({
            orderId,
            transactionAccount,
            ...(offeringIds && offeringIds.length > 0
                ? { offeringId: offeringIds.join(COMMA) }
                : {}),
            ...(orgId ? { orgId } : {}),
            ...(entitlementId ? { entitlementId } : {}),
            ...(siteIds && siteIds.length > 0
                ? { siteIds: siteIds.join(COMMA) }
                : {}),
            ...(directBuyStatus ? { directBuyStatus } : {}),
            ...(containsSiteProduct ? { containsSiteProduct: "true" } : {})
        });
        window.location.assign(
            window.location.origin + `/buy/cloud/org-setup?${searchParams}`
        );
    };

    const redirectSiteSetup = ({
        offeringIds,
        orgId,
        directBuyStatus
    }: {
        offeringIds: string[];
        orgId?: string;
        directBuyStatus?: DirectBuyStatus;
    }) => {
        const searchParams = new URLSearchParams({
            orderId,
            transactionAccount,
            ...(offeringIds && offeringIds.length > 0
                ? { offeringId: offeringIds.join(COMMA) }
                : {}),
            ...(orgId ? { orgId } : {}),
            ...(directBuyStatus ? { directBuyStatus } : {})
        });
        window.location.assign(
            window.location.origin + `/buy/cloud/site-setup?${searchParams}`
        );
    };

    return (
        <PostOrderProvisioningContainer>
            {isLoading ? <Loading /> : <ErrorView />}
        </PostOrderProvisioningContainer>
    );
};
